import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Snackbar,
  Alert,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Room as RoomIcon } from "@mui/icons-material";
import Layout from "../common/Layout";
import { labAPI, bookingAPI } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const LabManagement = () => {
  const { userProfile } = useAuth();
  // State for labs data
  const [labs, setLabs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState({
    labs: true,
    bookings: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // UI state
  const [activeTab, setActiveTab] = useState(0); // 0: Labs, 1: Equipment Assignment, 2: Bookings
  const [search, setSearch] = useState("");

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedLab, setSelectedLab] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    department: "",
    location: {
      building: "",
      floor: "",
      room: "",
    },
    capacity: 20,
    description: "",
    isActive: true,
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchLabs();
  }, []);
  // Auto-fetch bookings when switching to Bookings tab or selecting a different lab
  useEffect(() => {
    if (selectedLab && activeTab === 1) {
      fetchBookings(selectedLab._id);
    }
  }, [selectedLab, activeTab]);

  // Fetch labs data
  const fetchLabs = async () => {
    setLoading((prev) => ({ ...prev, labs: true }));
    try {
      const response = await labAPI.getAll();
      const labsData = response.data.map((lab) => ({
        ...lab,
        _id: lab.id,
      }));
      setLabs(labsData);
    } catch (err) {
      console.error("Error fetching labs:", err);
      setSnackbar({
        open: true,
        message: "Failed to load labs. Please try again.",
        severity: "error",
      });
      // Clear labs list on error
      setLabs([]);
    } finally {
      setLoading((prev) => ({ ...prev, labs: false }));
    }
  };

  // Fetch lab bookings
  const fetchBookings = async (labId) => {
    setLoading((prev) => ({ ...prev, bookings: true }));
    try {
      // Fetch all bookings then filter for this lab session
      const response = await bookingAPI.getAll();
      // Filter bookings for this lab session (handle both string and object resourceId)
      const filtered = response.data.filter(
        (b) =>
          b.resourceType === "lab_session" &&
          (b.resourceId === labId ||
            (typeof b.resourceId === "object" &&
              (b.resourceId.id === labId || b.resourceId._id === labId)))
      );
      // Normalize bookings: map id to _id and convert timestamps
      const allBookings = filtered.map((b) => {
        // Convert Firestore Timestamp or string to ISO string
        const toIso = (ts) => {
          if (!ts) return null;
          if (ts.seconds) {
            return new Date(ts.seconds * 1000).toISOString();
          }
          return new Date(ts).toISOString();
        };
        // Normalize userId for display
        let userObj = b.userId;
        if (typeof userObj === "string") {
          userObj = { name: userObj };
        } else if (!userObj) {
          userObj = { name: "Unknown User" };
        }
        return {
          ...b,
          _id: b.id || b._id,
          startTime: toIso(b.startTime),
          endTime: toIso(b.endTime),
          userId: userObj,
        };
      });
      setBookings(allBookings);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading((prev) => ({ ...prev, bookings: false }));
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // When switching to 'Bookings' tab (index 1), load bookings for the selected lab
    if (newValue === 1 && selectedLab) {
      fetchBookings(selectedLab._id);
    }
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearch(e.target.value.toLowerCase());
  };

  // Filter labs based on search query
  const filteredLabs = labs.filter((lab) => {
    // Filter by search text
    const locationStr = `${lab.location.building} ${lab.location.floor} ${lab.location.room}`;
    return (
      lab.name.toLowerCase().includes(search) ||
      lab.department.toLowerCase().includes(search) ||
      locationStr.toLowerCase().includes(search)
    );
  });

  // Open dialog to add new lab
  const handleAddLab = () => {
    setDialogMode("add");
    setFormData({
      name: "",
      department: "",
      location: {
        building: "",
        floor: "",
        room: "",
      },
      capacity: 20,
      description: "",
      isActive: true,
    });
    setOpenDialog(true);
  };

  // Open dialog to edit lab
  const handleEditLab = (lab) => {
    setDialogMode("edit");
    setSelectedLab(lab);
    setFormData({
      name: lab.name,
      department: lab.department,
      location: {
        building: lab.location?.building || "",
        floor: lab.location?.floor || "",
        room: lab.location?.room || "",
      },
      capacity: lab.capacity,
      description: lab.description || "",
      isActive: lab.isActive !== undefined ? lab.isActive : true,
    });
    setOpenDialog(true);
  };

  // Open dialog to view lab details
  const handleViewLab = (lab) => {
    setSelectedLab(lab);
    setActiveTab(0); // Switch to lab details tab
  };

  // Open confirmation dialog to delete lab
  const handleDeleteConfirm = (lab) => {
    setSelectedLab(lab);
    setDeleteConfirmOpen(true);
  };

  // Close dialogs
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteConfirmOpen(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested location object properties
    if (name.startsWith("location.")) {
      const locationField = name.split(".")[1];
      setFormData({
        ...formData,
        location: {
          ...formData.location,
          [locationField]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "capacity" ? Number(value) : value,
      });
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  // Submit form to add or edit lab
  const handleSubmit = async () => {
    try {
      let payload = { ...formData };
      if (dialogMode === "add") {
        // Always use departmentId from userProfile for new labs
        if (userProfile && userProfile.departmentId) {
          payload.departmentId = userProfile.departmentId;
        } else {
          setSnackbar({
            open: true,
            message:
              "Error: Your profile does not have a department assigned. Please contact a developer master.",
            severity: "error",
          });
          return;
        }
        await labAPI.create(payload);
        setSnackbar({
          open: true,
          message: "Laboratory added successfully",
          severity: "success",
        });
      } else {
        await labAPI.update(selectedLab._id, formData);
        setSnackbar({
          open: true,
          message: "Laboratory updated successfully",
          severity: "success",
        });
      }

      handleCloseDialog();
      fetchLabs(); // Refresh labs list
    } catch (err) {
      console.error("Error submitting form:", err);
      setSnackbar({
        open: true,
        message: `Failed to ${
          dialogMode === "add" ? "add" : "update"
        } laboratory. Please try again.`,
        severity: "error",
      });
    }
  };

  // Delete a lab
  const handleDelete = async () => {
    try {
      // Use Firestore document ID; fallback if `_id` is undefined
      const labId = selectedLab._id || selectedLab.id;
      await labAPI.delete(labId);
      setSnackbar({
        open: true,
        message: "Laboratory deleted successfully",
        severity: "success",
      });

      setDeleteConfirmOpen(false);
      setSelectedLab(null);
      fetchLabs(); // Refresh labs list
    } catch (err) {
      console.error("Error deleting lab:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete laboratory. Please try again.",
        severity: "error",
      });
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Render bookings for a lab
  const renderBookings = () => {
    if (!selectedLab) return <Typography>Please select a lab first</Typography>;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Bookings for {selectedLab.name}</Typography>
          <Button
            variant="contained"
            startIcon={<CalendarIcon />}
            href="/bookings/create"
          >
            Create Booking
          </Button>
        </Box>

        {loading.bookings ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : bookings.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No bookings found for this laboratory
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((booking) => (
                  <TableRow key={booking._id}>
                    <TableCell>
                      {booking.userId?.name || "Unknown User"}
                    </TableCell>
                    <TableCell>{booking.purpose}</TableCell>
                    <TableCell>{formatDateTime(booking.startTime)}</TableCell>
                    <TableCell>{formatDateTime(booking.endTime)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          booking.status.charAt(0).toUpperCase() +
                          booking.status.slice(1)
                        }
                        color={
                          booking.status === "approved"
                            ? "success"
                            : booking.status === "pending"
                            ? "warning"
                            : booking.status === "rejected"
                            ? "error"
                            : "default"
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // Render lab details and management tabs
  const renderLabDetails = () => {
    if (!selectedLab) {
      return (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Laboratory Selected
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please select a laboratory from the list to view its details
          </Typography>
        </Paper>
      );
    }

    return (
      <Box>
        <Paper sx={{ p: 0, mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Details" />
            <Tab label="Bookings" />
          </Tabs>
        </Paper>

        <Box role="tabpanel" hidden={activeTab !== 0}>
          {activeTab === 0 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {selectedLab.name}
                  {selectedLab.isActive === false && (
                    <Chip
                      label="Inactive"
                      size="small"
                      sx={{ ml: 2 }}
                      color="default"
                    />
                  )}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {selectedLab.department}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {selectedLab.location.building},{" "}
                    {selectedLab.location.floor}, Room{" "}
                    {selectedLab.location.room}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Capacity
                  </Typography>
                  <Typography variant="body1">
                    {selectedLab.capacity} persons
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {selectedLab.description || "No description available"}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditLab(selectedLab)}
                >
                  Edit Laboratory
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteConfirm(selectedLab)}
                >
                  Delete
                </Button>
              </Box>
            </Paper>
          )}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 1}>
          {activeTab === 1 && renderBookings()}
        </Box>
      </Box>
    );
  };

  return (
    <Layout title="Laboratory Management">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Laboratory Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage laboratories, assign equipment, and view bookings
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left panel - Labs list */}
        <Grid item xs={12} md={5} lg={4}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Laboratories</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddLab}
              >
                Add Lab
              </Button>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search laboratories..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {loading.labs ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : labs.length === 0 ? (
              <Typography align="center" color="text.secondary">
                No laboratories found
              </Typography>
            ) : filteredLabs.length === 0 ? (
              <Typography align="center" color="text.secondary">
                No laboratories match your search
              </Typography>
            ) : (
              <Box sx={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                {filteredLabs.map((lab) => (
                  <Paper
                    key={lab._id}
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: "pointer",
                      bgcolor:
                        selectedLab?._id === lab._id
                          ? "action.selected"
                          : "background.paper",
                      borderLeft:
                        lab.isActive === false
                          ? "4px solid #9e9e9e"
                          : "4px solid #4caf50",
                    }}
                    onClick={() => handleViewLab(lab)}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      gutterBottom
                    >
                      {lab.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {lab.department}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <RoomIcon
                        fontSize="small"
                        sx={{ mr: 0.5, color: "text.secondary" }}
                      />
                      <Typography variant="body2" noWrap>
                        {lab.location.building}, Room {lab.location.room}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right panel - Lab details, equipment, bookings */}
        <Grid item xs={12} md={7} lg={8}>
          {renderLabDetails()}
        </Grid>
      </Grid>

      {/* Add/Edit Lab Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "add" ? "Add New Laboratory" : "Edit Laboratory"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Laboratory Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Location Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Building"
                name="location.building"
                value={formData.location.building}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Floor"
                name="location.floor"
                value={formData.location.floor}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Room Number"
                name="location.room"
                value={formData.location.room}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Capacity"
                name="capacity"
                type="number"
                value={formData.capacity}
                onChange={handleInputChange}
                required
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={handleCheckboxChange}
                    name="isActive"
                  />
                }
                label="Laboratory is currently active"
                sx={{ mt: 3 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === "add" ? "Add Laboratory" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the laboratory "{selectedLab?.name}
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default LabManagement;
